import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { actions } from './redux';
import Api from '../../utils/Api';
import { addItem as notify } from '../notifications/actions';

// `fn` is the program function of the tab being loaded (CREW-1187). It
// scopes the DATA the same way `?function=` already scoped the picker
// (CREW-1158): without it, "All" on the Recruiting tab lists everyone with
// an open task, because `hasActivity` counts tasks and `buildRows` grows a
// row from any of the three datasets. Omitted when absent, so the payouts
// tab and any older caller keep the exact query they had.
export const asQuery = (clientIds, month, fn) => {
  const q = clientIds && clientIds.length ? { client_ids: clientIds.join(',') } : {};
  const withMonth = month ? { ...q, month } : q;
  return fn ? { ...withMonth, function: fn } : withMonth;
};

// The Results tab's query: owners and fleet managers as id lists, the period
// as dates; anything unset is left out so the BE applies its defaults.
export const resultsQuery = (filter) => {
  const q = {};
  if (filter?.ownerIds?.length) q.owner_ids = filter.ownerIds.join(',');
  if (filter?.managerIds?.length) q.manager_ids = filter.managerIds.join(',');
  if (filter?.from) q.from = filter.from;
  if (filter?.to) q.to = filter.to;
  return q;
};

// The employee picker can be scoped to a program tab via `?function=`
// (CREW-1158); no argument keeps the old unfiltered list.
export function* loadRecruiters({ payload } = {}) {
  const query = payload?.function ? { function: payload.function } : {};
  try {
    const data = yield call(Api.recruitingMetrics.getRecruiters, query);
    yield put(actions.setRecruiters(data));
  } catch (e) {
    console.error(e);
    yield put(notify({ type: 'error', title: 'Error', message: 'Could not load employees' }));
  }
}

// takeLatest: switching the employee filter quickly must cancel the
// previous in-flight dashboard load so stale data can't overwrite fresh.
// Each program tab loads its own dataset pair (CREW-1157): recruiting keeps
// the original three requests; coordination fetches its counters + overdue.
export function* loadDashboard({ payload }) {
  const clientIds = payload?.clientIds || [];
  const month = payload?.month || null;
  const coordination = payload?.tab === 'coordination';
  // The Looker pages moved in (CREW-1312 / 1314): Results has its own filter
  // (owners, fleet managers, period), Promotion shares the recruiting picker.
  const results = payload?.tab === 'results';
  const promotion = payload?.tab === 'promotion';
  // The payouts/settings tab renders no program datasets — but it does need
  // the settings, and the tab is persisted, so a reload straight onto it must
  // still fill them in.
  const settingsOnly = payload?.tab === 'payouts';
  // Both queries carry the tab's function: `tasks_current` and
  // `overdue_days` are shared between the tabs (coordination reuses the
  // overdue dataset), so the scope has to travel with the request rather
  // than be pinned per endpoint on the backend.
  const fn = coordination ? 'coordination' : 'recruiting';
  const liveQuery = asQuery(clientIds, null, fn);
  const periodQuery = asQuery(clientIds, month, fn);
  try {
    // NOT setError(null): a global saga (authorization/sagas.js) watches every
    // action whose type contains "_ERROR" and does `payload.response` — a null
    // payload throws there and takes down the whole saga middleware. An empty
    // string is falsy for the FE's `{error && <Alert>}` check but safe to touch.
    yield put(actions.setError(''));
    yield put(actions.setLoading(true));
    if (settingsOnly) {
      yield put(actions.setSettings(yield call(Api.recruitingMetrics.getSettings)));
    } else if (results) {
      yield put(actions.setResults(yield call(Api.recruitingMetrics.getResults, resultsQuery(payload?.results))));
    } else if (promotion) {
      const [data, settings] = yield all([
        call(Api.recruitingMetrics.getPromotion, periodQuery),
        call(Api.recruitingMetrics.getSettings),
      ]);
      yield put(actions.setPromotion(data));
      yield put(actions.setSettings(settings));
    } else if (coordination) {
      const [stats, charts, overdue, settings] = yield all([
        call(Api.recruitingMetrics.getCoordination, periodQuery),
        call(Api.recruitingMetrics.getCoordinationCharts, periodQuery),
        call(Api.recruitingMetrics.getOverdueDays, periodQuery),
        call(Api.recruitingMetrics.getSettings),
      ]);
      yield put(actions.setCoordination(stats));
      yield put(actions.setCoordinationCharts(charts));
      yield put(actions.setOverdue(overdue));
      yield put(actions.setSettings(settings));
    } else {
      const [summary, tasks, overdue, settings] = yield all([
        call(Api.recruitingMetrics.getSummary, periodQuery),
        call(Api.recruitingMetrics.getCurrentTasks, liveQuery),
        call(Api.recruitingMetrics.getOverdueDays, periodQuery),
        call(Api.recruitingMetrics.getSettings),
      ]);
      yield put(actions.setSummary(summary));
      yield put(actions.setTasks(tasks));
      yield put(actions.setOverdue(overdue));
      yield put(actions.setSettings(settings));
    }
  } catch (e) {
    console.error(e);
    yield put(actions.setError(e?.message || 'Failed to load recruiting metrics'));
  } finally {
    // In `finally`, not at the end of the try: a rejected request has to clear
    // the flag too. `isLoading` dims the tab body and sets pointerEvents:'none'
    // (component/index.jsx), so a flag left raised freezes the page until some
    // other action triggers a reload. takeLatest cancellation lands here as
    // well, and that is fine — the replacing load raises the flag after.
    yield put(actions.setLoading(false));
  }
}

// CREW-1159 — freeze the month into the ledger, then reload the history
// matrix, the only screen that reads it. The program tabs are computed live
// (summary / coordination / overdue never touch the ledger), so closing a
// month changes nothing there and re-fetching them would be pure noise.
export function* closeMonth({ payload } = {}) {
  try {
    const result = yield call(Api.recruitingMetrics.closeMonth, payload?.month);
    yield put(notify({
      type: 'success', title: 'Month closed',
      message: `${result?.month || payload?.month}: ${result?.rows_written ?? 0} rows written`,
    }));
    yield put(actions.loadBonusHistoryRequest({ months: 6 }));
  } catch (e) {
    console.error(e);
    const detail = e?.apiErrors?.[0]?.detail;
    yield put(notify({ type: 'error', title: 'Error', message: detail || 'Could not close the month' }));
  }
}

export function* loadBonusHistory({ payload } = {}) {
  try {
    const data = yield call(Api.recruitingMetrics.getBonusHistory, { months: payload?.months || 6 });
    yield put(actions.setBonusHistory(data));
  } catch (e) {
    console.error(e);
    yield put(notify({ type: 'error', title: 'Error', message: 'Could not load the payout history' }));
  }
}

function* saveSettings({ payload }) {
  try {
    const settings = yield call(Api.recruitingMetrics.updateSettings, payload);
    yield put(actions.setSettings(settings));
    yield put(notify({ type: 'success', title: 'Success', message: 'KPI settings saved' }));
  } catch (e) {
    console.error(e);
    yield put(notify({ type: 'error', title: 'Error', message: 'Could not save KPI settings' }));
  }
}

function* loadBonusReport({ payload }) {
  try {
    const data = yield call(Api.recruitingMetrics.getBonusReport, payload?.month ? { month: payload.month } : {});
    yield put(actions.setBonusReport(data));
  } catch (e) {
    console.error(e);
    yield put(notify({ type: 'error', title: 'Error', message: 'Could not load bonus report' }));
  }
}

export default function getFeatureSagas() {
  return [
    takeEvery(actions.loadRecruitersRequest.type, loadRecruiters),
    takeLatest(actions.loadDashboardRequest.type, loadDashboard),
    takeEvery(actions.saveSettingsRequest.type, saveSettings),
    takeEvery(actions.closeMonthRequest.type, closeMonth),
    takeLatest(actions.loadBonusHistoryRequest.type, loadBonusHistory),
    takeLatest(actions.loadBonusReportRequest.type, loadBonusReport),
  ];
}
